import { useEffect, useRef, useState } from "react";
import { useInView, motion, useSpring } from "framer-motion";
import type { StatCounterConfig } from "@site-factory/config";

export interface CountUpProps extends StatCounterConfig {
  className?: string;
}

/**
 * Formats a number with thousand separators.
 */
function formatNumber(
  value: number,
  decimals: number = 0,
  separator: string = ","
): string {
  const parts = value.toFixed(decimals).split(".");
  parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, separator);
  return parts.join(".");
}

/**
 * Animated counter that counts up from one number to another.
 * Triggers when scrolled into view.
 *
 * @example
 * ```tsx
 * <CountUp to={500} prefix="$" suffix="+" duration={2} decimals={0} />
 * // Renders: $500+
 * ```
 */
export function CountUp({
  from = 0,
  to,
  duration = 2,
  decimals = 0,
  prefix = "",
  suffix = "",
  separator = ",",
  delay = 0,
  className,
}: CountUpProps) {
  const ref = useRef<HTMLSpanElement>(null);
  const isInView = useInView(ref, { once: true, amount: 0 });
  const [hasAnimated, setHasAnimated] = useState(false);

  // Respect user's reduced motion preference
  const prefersReducedMotion =
    typeof window !== "undefined" &&
    window.matchMedia("(prefers-reduced-motion: reduce)").matches;

  // Spring animation for smooth counting
  const motionValue = useSpring(from, {
    duration: duration * 1000, // convert to ms
    bounce: 0,
  });

  // Transform the motion value to a display string
  const [displayValue, setDisplayValue] = useState(
    formatNumber(from, decimals, separator)
  );

  useEffect(() => {
    if (isInView && !hasAnimated) {
      setHasAnimated(true);

      // Delay the animation if specified
      const timeout = setTimeout(() => {
        motionValue.set(to);
      }, delay * 1000);

      return () => clearTimeout(timeout);
    }
  }, [isInView, hasAnimated, motionValue, to, delay]);

  useEffect(() => {
    const unsubscribe = motionValue.on("change", (latest) => {
      setDisplayValue(formatNumber(latest, decimals, separator));
    });

    return unsubscribe;
  }, [motionValue, decimals, separator]);

  // If user prefers reduced motion, show final value immediately when in view
  if (prefersReducedMotion) {
    return (
      <span ref={ref} className={className}>
        {prefix}
        {isInView ? formatNumber(to, decimals, separator) : formatNumber(from, decimals, separator)}
        {suffix}
      </span>
    );
  }

  return (
    <motion.span ref={ref} className={className}>
      {prefix}
      {displayValue}
      {suffix}
    </motion.span>
  );
}

/**
 * Hook version of CountUp for more flexibility.
 * Returns the current animated value.
 *
 * @example
 * ```tsx
 * const count = useCountUp({ to: 100, duration: 2 });
 * return <span>{count}</span>;
 * ```
 */
export function useCountUp({
  from = 0,
  to,
  duration = 2,
  decimals = 0,
  separator = ",",
  enabled = true,
}: Omit<StatCounterConfig, "prefix" | "suffix"> & { enabled?: boolean }): string {
  const motionValue = useSpring(from, {
    duration: duration * 1000,
    bounce: 0,
  });

  const [displayValue, setDisplayValue] = useState(
    formatNumber(from, decimals, separator)
  );

  useEffect(() => {
    if (enabled) {
      motionValue.set(to);
    }
  }, [enabled, motionValue, to]);

  useEffect(() => {
    const unsubscribe = motionValue.on("change", (latest) => {
      setDisplayValue(formatNumber(latest, decimals, separator));
    });

    return unsubscribe;
  }, [motionValue, decimals, separator]);

  return displayValue;
}
